import { Resource } from "@opentelemetry/resources";
import type { SpanProcessor } from "@opentelemetry/sdk-trace-base";
import { SimpleSpanProcessor } from "@opentelemetry/sdk-trace-base";
import {
  BatchSpanProcessor,
  BatchSpanProcessorBrowserConfig,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";
import Bowser from "bowser";
import { ClientJS } from "clientjs";

import { PACKAGE_VERSION } from "./constants";
import { ProviderConfig, ProviderType } from "./types";
import { getSampleRateRatio } from "./utils";
import { FilteringSpanProcessor } from "./utils/FilteringSpanProcessor";
import { HeadersInBodyOTLPTraceExporter } from "./utils/HeadersInBodyOTLPTraceExporter";
import { configureLogger, error as logError, log, warn } from "./utils/logger";

type ProviderLogLevel = "info" | "warn" | "error";

type ClientGeoLocation = {
  country?: string;
  city?: string;
  county?: string;
};

type ClientGeoAttributes = Record<string, string | number | boolean>;

type ClientNetworkInfo = {
  ipAddress?: string;
  geoLocation?: ClientGeoLocation;
  geoAttributes?: ClientGeoAttributes;
};

const emitProviderLog = (
  level: ProviderLogLevel,
  message: string,
  attributes: Record<string, unknown> = {},
  err?: unknown,
) => {
  const payload = {
    message,
    attributes: {
      "l9rum.provider.component": "ProviderClass",
      ...attributes,
    },
    ...(err ? { error: err } : {}),
  };

  if (level === "warn") {
    warn(payload);
    return;
  }

  if (level === "error") {
    logError(payload);
    return;
  }

  log(payload);
};

class ProviderClass {
  provider: ProviderType;
  client?: ClientJS;
  static entryPage: Record<string, string> = {};
  static resourceAttrs: Record<string, any> = {};

  constructor(config: ProviderConfig, networkInfo?: ClientNetworkInfo) {
    emitProviderLog("info", "[L9RUM Provider] Constructing provider", {
      "l9rum.provider.phase": "constructor",
    });

    this.client = this._createClient();

    if (!ProviderClass.entryPage.url) {
      try {
        ProviderClass.entryPage = this._getEntryPageAttrs();
      } catch (err) {
        emitProviderLog(
          "warn",
          "[L9RUM Provider] Failed to compute entry page attributes",
          {
            "l9rum.provider.phase": "constructor",
            "l9rum.provider.guard": "entry_page",
          },
          err,
        );
        ProviderClass.entryPage = {};
      }
    }

    if (!ProviderClass.resourceAttrs["user_agent.original"]) {
      const { serviceName, deploymentEnvironment, appVersion, ...restResourceAttributes } =
        config.resourceAttributes || {};

      ProviderClass.resourceAttrs = this._getResourceAttrs(restResourceAttributes, networkInfo);

      emitProviderLog("info", "[L9RUM Provider] Derived static resource attributes", {
        "l9rum.provider.phase": "constructor",
        "l9rum.provider.service_name": serviceName,
        "l9rum.provider.environment": deploymentEnvironment,
        "l9rum.provider.app_version": appVersion,
        "l9rum.provider.guard": "resource_attrs",
      });
    }

    this.provider = this._init(config);

    try {
      this.provider.register();
      emitProviderLog("info", "[L9RUM Provider] Provider registered", {
        "l9rum.provider.phase": "constructor",
        "l9rum.provider.guard": "register",
      });
    } catch (err) {
      emitProviderLog(
        "error",
        "[L9RUM Provider] Failed to register provider",
        {
          "l9rum.provider.phase": "constructor",
          "l9rum.provider.guard": "register",
        },
        err,
      );
      throw err;
    }
  }

  private _createClient(): ClientJS | undefined {
    try {
      return new ClientJS();
    } catch (err) {
      emitProviderLog(
        "warn",
        "[L9RUM Provider] Failed to instantiate ClientJS; continuing without fingerprint support",
        {
          "l9rum.provider.phase": "constructor",
          "l9rum.provider.guard": "client_init",
        },
        err,
      );
      return undefined;
    }
  }

  private _getResourceAttrs(resourceAttributes: any, networkInfo?: ClientNetworkInfo) {
    try {
      const nav = navigator as any;
      const win = window as any;
      const ua = navigator.userAgent || "";
      const parser = Bowser.getParser(ua);
      const result = parser.getResult();
      const browserName = parser.getBrowserName() || result?.browser?.name;
      const browserVersion = parser.getBrowserVersion() || result?.browser?.version;
      const osName = result?.os?.name;
      const osVersion = result?.os?.version;
      const osVersionName = (result as any)?.os?.versionName;
      const engine = parser.getEngine();
      const engineName = engine?.name || result?.engine?.name;
      const engineVersion = engine?.version || result?.engine?.version;
      const platformType = parser.getPlatformType() || result?.platform?.type; // desktop|mobile|tablet|tv|wearable|embedded
      const deviceVendor = (result as any)?.platform?.vendor;
      const deviceModel = (result as any)?.platform?.model;
      const screenSize = win.screen?.width
        ? win.screen.width < 768
          ? "small"
          : win.screen.width < 1024
            ? "medium"
            : "large"
        : "unknown";
      const attrs = {
        ...resourceAttributes,
        "l9.sdk.version": PACKAGE_VERSION,
        "user_agent.original": navigator.userAgent,
        // Prefer Bowser for reliability; fall back to UA-CH or clientjs
        "browser.mobile": platformType
          ? platformType === "mobile" || platformType === "tablet"
          : (nav.userAgentData?.mobile ?? this.client?.isMobile?.()),
        "browser.touch_screen_enabled":
          "maxTouchPoints" in navigator && navigator.maxTouchPoints > 0,
        "browser.language": navigator.language,
        // Use Bowser for browser + OS; keep previous keys for compatibility
        "browser.name":
          browserName || nav.userAgentData?.brands?.[0]?.brand || this.client?.getBrowser?.(),
        "browser.version":
          browserVersion ||
          nav.userAgentData?.brands?.[0]?.version ||
          this.client?.getBrowserMajorVersion?.(),
        "browser.platform": osName || nav.userAgentData?.platform || this.client?.getOS?.(),
        // Extra, more precise attributes
        "browser.engine.name": engineName,
        "browser.engine.version": engineVersion,
        "os.name": osName,
        "os.version": osVersion,
        "os.version_name": osVersionName,
        "browser.brands": nav.userAgentData?.brands
          ? JSON.stringify(nav.userAgentData.brands)
          : undefined,
        "device.type": platformType || this.client?.getDeviceType?.(),
        "device.vendor": deviceVendor,
        "device.model": deviceModel,
        "network.effectiveType": (navigator as any).connection?.effectiveType,
        "screen.width": win.screen?.width,
        "screen.height": win.screen?.height,
        "screen.size": screenSize,
        "browser.width": win.innerWidth,
        "browser.height": win.innerHeight,
        ...(networkInfo?.ipAddress ? { "client.ip": networkInfo.ipAddress } : {}),
        ...(networkInfo?.geoLocation?.country
          ? { "enduser.geo.country": networkInfo.geoLocation.country }
          : {}),
        ...(networkInfo?.geoLocation?.city
          ? { "enduser.geo.city": networkInfo.geoLocation.city }
          : {}),
        ...(networkInfo?.geoLocation?.county
          ? { "enduser.geo.county": networkInfo.geoLocation.county }
          : {}),
        ...(networkInfo?.geoAttributes
          ? Object.fromEntries(
              Object.entries(networkInfo.geoAttributes).map(([key, value]) => [
                `enduser.geo.${key}`,
                value,
              ]),
            )
          : {}),
        ...ProviderClass.entryPage,
      };
      // Remove attributes that are null, undefined, or empty string
      try {
        return Object.fromEntries(
          Object.entries(attrs).filter(([, v]) => v !== undefined && v !== null && v !== ""),
        );
      } catch (err) {
        emitProviderLog(
          "warn",
          "[L9RUM Provider] Failed to sanitize resource attributes",
          {
            "l9rum.provider.phase": "constructor",
            "l9rum.provider.guard": "resource_attrs_sanitize",
          },
          err,
        );
        return {};
      }
    } catch (err) {
      emitProviderLog(
        "warn",
        "[L9RUM Provider] Failed to derive resource attributes",
        {
          "l9rum.provider.phase": "constructor",
          "l9rum.provider.guard": "resource_attrs",
        },
        err,
      );
      return {};
    }
  }

  private _getEntryPageAttrs() {
    return {
      "entry_page.url": window.location.href,
      "entry_page.path": window.location.pathname,
      "entry_page.search": window.location.search,
      "entry_page.hash": window.location.hash,
      "entry_page.hostname": window.location.hostname,
      "entry_page.referrer": document.referrer,
    };
  }

  private _init(config: ProviderConfig) {
    const { sampleRate, resourceAttributes } = config;
    const { serviceName, deploymentEnvironment, appVersion, ...restResourceAttributes } =
      resourceAttributes || {};

    const exporter = this._getExporter(config);
    const ratio = getSampleRateRatio(sampleRate);

    // Batch for general spans, tuned for browsers
    const resolvedMaxBatch =
      typeof config.maxExportBatchSize === "number" &&
      isFinite(config.maxExportBatchSize) &&
      config.maxExportBatchSize > 0
        ? Math.floor(config.maxExportBatchSize)
        : 100; // default
    const batch = new BatchSpanProcessor(exporter, {
      disableAutoFlushOnDocumentHide: false,
      // Bound batch sizes to avoid oversized payloads in browsers
      maxExportBatchSize: resolvedMaxBatch,
      maxQueueSize: 2048,
    } as BatchSpanProcessorBrowserConfig);

    // Simple for critical spans (export immediately)
    const simple = new SimpleSpanProcessor(exporter);

    // Route spans: critical ones via Simple, others via Batch
    const isCritical = (span: any) => {
      const attrs = (span && (span as any).attributes) || {};
      return attrs["span.critical"] === true || attrs["l9.critical"] === true;
    };

    const processors: SpanProcessor[] = [
      new FilteringSpanProcessor(simple, (span) => {
        return isCritical(span);
      }),
      new FilteringSpanProcessor(batch, (span) => {
        return !isCritical(span);
      }),
      ...(config.extraSpanProcessors ?? []),
    ];

    const provider = new WebTracerProvider({
      resource: new Resource({
        ...ProviderClass.resourceAttrs,
        "service.name": serviceName,
        "deployment.environment": deploymentEnvironment,
        "app.version": appVersion,
        ...restResourceAttributes,
      }),
      sampler: new ParentBasedSampler({
        root: new TraceIdRatioBasedSampler(ratio),
      }),
      spanProcessors: processors,
    });

    emitProviderLog("info", "[L9RUM Provider] WebTracerProvider initialized", {
      "l9rum.provider.phase": "constructor",
      "l9rum.provider.guard": "provider_init",
      "l9rum.provider.sample_ratio": ratio,
      "l9rum.provider.max_export_batch_size": resolvedMaxBatch,
    });

    return provider;
  }

  private _getOrigin() {
    try {
      return window.location.origin;
    } catch (err) {
      emitProviderLog(
        "warn",
        "[L9RUM Provider] Failed to read window.origin",
        {
          "l9rum.provider.phase": "constructor",
          "l9rum.provider.guard": "origin",
        },
        err,
      );
      return undefined;
    }
  }

  private _getClientId() {
    try {
      const fingerprint = this.client?.getFingerprint?.();
      return fingerprint ? fingerprint.toString() : undefined;
    } catch (err) {
      emitProviderLog(
        "warn",
        "[L9RUM Provider] Failed to compute Client-ID fingerprint",
        {
          "l9rum.provider.phase": "constructor",
          "l9rum.provider.guard": "client_id",
        },
        err,
      );
      return undefined;
    }
  }

  private _getExporter(config: ProviderConfig) {
    const { clientToken, ...restHeaders } = config.headers;
    const finalEndpoint = (() => {
      if (config.baseUrl && typeof config.baseUrl === "string" && config.baseUrl.trim()) {
        const trimmed = config.baseUrl.replace(/\/+$/, "");
        return `${trimmed}/telemetry/beacon/v1/traces`;
      }
      const errorMessage =
        "L9RUM: `baseUrl` must be provided. It should look like https://host/v1/otlp/organizations/<org>.";
      emitProviderLog(
        "error",
        "[L9RUM Provider] Missing baseUrl in provider config",
        {
          "l9rum.provider.phase": "constructor",
          "l9rum.provider.guard": "base_url",
        },
        new Error(errorMessage),
      );
      throw new Error(errorMessage);
    })();
    const headers: Record<string, string> = {
      "X-LAST9-API-TOKEN": `Bearer ${clientToken}`,
      ...restHeaders,
    };
    const origin = this._getOrigin();
    if (origin) {
      headers["X-LAST9-ORIGIN"] = origin;
    }
    const clientId = this._getClientId();
    if (clientId) {
      headers["Client-ID"] = clientId;
    }

    const exporter = new HeadersInBodyOTLPTraceExporter({ url: finalEndpoint }, headers);

    emitProviderLog("info", "[L9RUM Provider] Trace exporter created", {
      "l9rum.provider.phase": "constructor",
      "l9rum.provider.guard": "exporter",
      "l9rum.provider.export_url": finalEndpoint,
    });

    return exporter;
  }
}

export const initProvider = async (config: ProviderConfig) => {
  emitProviderLog("info", "[L9RUM Provider] initProvider invoked", {
    "l9rum.provider.phase": "init_provider",
  });

  const networkInfo: ClientNetworkInfo = {};
  const geoEnabled = config.geoIpEnabled === true;
  if (!geoEnabled) {
    emitProviderLog("info", "[L9RUM Provider] GeoJS lookup disabled by configuration", {
      "l9rum.provider.phase": "init_provider",
      "l9rum.provider.guard": "geo_disabled",
    });
  } else {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      const resp = await fetch("https://get.geojs.io/v1/ip/geo.json", {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!resp.ok) {
        emitProviderLog("warn", "[L9RUM Provider] GeoJS request failed", {
          "l9rum.provider.phase": "init_provider",
          "l9rum.provider.guard": "geo_fetch_status",
          "http.status_code": resp.status,
        });
      } else {
        const data = await resp.json();
        if (data && typeof data === "object" && !Array.isArray(data)) {
          const sanitizedEntries = Object.entries(data).reduce<ClientGeoAttributes>(
            (acc, [key, value]) => {
              if (value === null || value === undefined) {
                return acc;
              }
              if (typeof value === "string") {
                const trimmed = value.trim();
                if (trimmed.length === 0) {
                  return acc;
                }
                acc[key] = trimmed;
                return acc;
              }
              if (typeof value === "number" || typeof value === "boolean") {
                acc[key] = value;
              }
              return acc;
            },
            {} as ClientGeoAttributes,
          );

          const country = (() => {
            if (typeof sanitizedEntries.country === "string") {
              return sanitizedEntries.country;
            }
            if (typeof sanitizedEntries.country_name === "string") {
              return sanitizedEntries.country_name;
            }
            return undefined;
          })();

          const city =
            typeof sanitizedEntries.city === "string" ? sanitizedEntries.city : undefined;
          const county = (() => {
            if (typeof sanitizedEntries.county === "string") {
              return sanitizedEntries.county;
            }
            if (typeof sanitizedEntries.region === "string") {
              return sanitizedEntries.region;
            }
            if (typeof sanitizedEntries.region_name === "string") {
              return sanitizedEntries.region_name;
            }
            return undefined;
          })();

          const geoLocation = Object.fromEntries(
            Object.entries({ country, city, county }).filter(
              ([, value]) => typeof value === "string" && value.trim().length > 0,
            ),
          ) as ClientGeoLocation;

          if (typeof sanitizedEntries.ip === "string") {
            networkInfo.ipAddress = sanitizedEntries.ip;
          }

          if (Object.keys(geoLocation).length > 0) {
            networkInfo.geoLocation = geoLocation;
          }

          if (Object.keys(sanitizedEntries).length > 0) {
            networkInfo.geoAttributes = sanitizedEntries;
          }

          emitProviderLog("info", "[L9RUM Provider] GeoJS attributes derived", {
            "l9rum.provider.phase": "init_provider",
            "l9rum.provider.guard": "geo_fetch",
            "l9rum.provider.geo_available": networkInfo.geoAttributes ? "true" : "false",
            "l9rum.provider.ip_available": networkInfo.ipAddress ? "true" : "false",
            "l9rum.provider.geo_attribute_count": networkInfo.geoAttributes
              ? Object.keys(networkInfo.geoAttributes).length
              : 0,
          });
        } else {
          emitProviderLog("warn", "[L9RUM Provider] GeoJS response was not an object", {
            "l9rum.provider.phase": "init_provider",
            "l9rum.provider.guard": "geo_fetch_shape",
          });
        }
      }
    } catch (err) {
      const isTimeout = err instanceof Error && err.name === "AbortError";
      emitProviderLog(
        "warn",
        isTimeout
          ? "[L9RUM Provider] GeoJS fetch timed out; continuing without geo attributes"
          : "[L9RUM Provider] Failed to fetch GeoJS attributes; continuing without them",
        {
          "l9rum.provider.phase": "init_provider",
          "l9rum.provider.guard": "geo_fetch_error",
          "l9rum.provider.geo_timeout": isTimeout ? "true" : "false",
        },
        err,
      );
    }
  }
  try {
    const providerInstance = new ProviderClass(config, networkInfo);
    const { serviceName, deploymentEnvironment, appVersion, ...restResourceAttributes } =
      config.resourceAttributes || {};

    const loggerResourceAttributes = {
      ...ProviderClass.resourceAttrs,
      "service.name": serviceName,
      "deployment.environment": deploymentEnvironment,
      "app.version": appVersion,
      ...restResourceAttributes,
    };

    configureLogger(
      config.headers.clientToken,
      config.baseUrl,
      serviceName,
      loggerResourceAttributes,
    );
    emitProviderLog("info", "[L9RUM Provider] initProvider completed", {
      "l9rum.provider.phase": "init_provider",
      "l9rum.provider.guard": "complete",
    });
    return providerInstance.provider;
  } catch (err) {
    emitProviderLog(
      "error",
      "[L9RUM Provider] Provider initialization failed",
      {
        "l9rum.provider.phase": "init_provider",
        "l9rum.provider.guard": "init_failed",
      },
      err,
    );
    throw err;
  }
};
